import React, { useMemo, useState } from 'react'

import moment from 'moment'
import styled from 'styled-components'

import { Box, Flex } from 'app/components/design-system-components'
import HorizontalLine from 'app/components/design-system-components/HorizontalLine'
import { SearchRounded } from 'app/components/design-system-components/icons/actions'
import { Input } from 'app/components/design-system-components/inputs/Input/Input'
import { Select } from 'app/components/design-system-components/inputs/Select/Select'
import { Text } from 'app/components/design-system-components/typography'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import { TeamMemberRow } from 'app/shared_components/AvatarAndNameDropdown/teamMemberRow'
import Pagination from 'app/shared_components/pagination/pagination'
import * as dateTimeHelpers from 'app/utils/datetime/helpers'

export const STATUS_ACCEPTED = 'accepted'

export const DEFAULT_PAGINATION_LIMIT = 5

const PAGE_SIZE_OPTIONS = [
  {
    optionMeta: { id: '5', displayText: '5' },
    value: DEFAULT_PAGINATION_LIMIT,
  },
  { optionMeta: { id: '10', displayText: '10' }, value: 10 },
  { optionMeta: { id: '20', displayText: '20' }, value: 20 },
]

const PaginationContainer = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.space[4]}px;
  padding-top: ${theme.space[3]}px;
  gap: ${theme.space[3]}px;
  position: relative;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.space[4]}px;
  }
`

const MembersList = ({
  teamMembersList,
  showToolsButton = false,
  memberTools,
  onClickHandler,
  showAvatar,
}) => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_LIMIT)
  const [searchText, setSearchText] = useState('')

  const filteredMembers = useMemo(() => {
    if (!searchText.trim()) {
      return teamMembersList
    }

    const lowerSearchText = searchText.toLowerCase()
    return teamMembersList.filter((member) => {
      const name = member.name?.toLowerCase() || ''
      const email = member.email?.toLowerCase() || ''
      const mobile = member.mobile?.toLowerCase() || ''

      return (
        name.includes(lowerSearchText) ||
        email.includes(lowerSearchText) ||
        mobile.includes(lowerSearchText)
      )
    })
  }, [teamMembersList, searchText])

  React.useEffect(() => {
    setPage(1)
  }, [searchText, pageSize])

  const startIndex = (page - 1) * pageSize
  const endIndex = Math.min(page * pageSize, filteredMembers.length)
  const viewList = filteredMembers.slice(startIndex, endIndex)

  const handlePageSizeChange = (selectedOption) => {
    setPageSize(selectedOption.value)
  }

  const handleSearchChange = ({ target }) => {
    setSearchText(target.value)
  }

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between">
      <Box mb={4} mt={6}>
        <Input
          icon={
            <SearchRounded
              style={{
                color: theme.colors.gray500,
                width: theme.width[6] + 'px',
                height: theme.width[6] + 'px',
              }}
            />
          }
          inputProps={{
            placeholder: 'Search by name, email, or phone',
            value: searchText,
            onChange: handleSearchChange,
          }}
          maxWidth={theme.width[30] * 2 + 'px'}
          mr={theme.space[1] + 'px'}
          height={theme.height[18]}
        />
      </Box>

      {viewList && viewList.length > 0 ? (
        viewList.map((teamMember, index) => {
          const memberName = teamMember.name
          const [firstName, ...restName] = memberName.split(' ')
          const { guid = '' } = teamMember

          const statusAccepted = teamMember.status === STATUS_ACCEPTED
          const translatedMemberData = {
            firstName,
            lastName: restName.join(' '),
            email: teamMember.email,
            mobileNumber: teamMember.mobile,
            status: teamMember.status,
            statusDate: moment(teamMember.statusDate).format(
              dateTimeHelpers.INVITED_DATE_FORMAT,
            ),
            isAdmin: teamMember.isAdmin,
            isTeamLeader: teamMember.isTeamLeader,
            isLeasingConsultant: teamMember.isLeasingConsultant,
            isPropertyManager: teamMember.isPropertyManager,
            isCasualInspector: teamMember.isCasualInspector,
            isBDM: teamMember.isBDM,
            isFinance: teamMember.isFinance,
            alias: teamMember.alias,
            invitedAt: moment(teamMember.invitedAt).format(
              'hh:MMa DD MMM YYYY',
            ),
            inviteExpiresAt: moment(teamMember.inviteExpiresAt)
              .fromNow()
              .replace('in ', ''),
            lastLogin:
              teamMember.lastLogin &&
              moment(teamMember.lastLogin).format('hh:MMa DD MMM YYYY'),
            lastLoginIP: teamMember.lastLoginIP,
            statusAccepted,
            guid,
            agentUtilityProviderUserID: teamMember?.agentUtilityProviderUserID,
          }
          return (
            <React.Fragment key={teamMember.guid || index}>
              <TeamMemberRow
                item={translatedMemberData}
                showStatusPills={false}
                showDates={true}
                showToolsButton={showToolsButton}
                memberTools={memberTools}
                onClickHandler={onClickHandler}
                showAvatar={showAvatar}
              />
              <HorizontalLine />
            </React.Fragment>
          )
        })
      ) : (
        <Box textAlign="center" p={4} color={theme.colors.gray600}>
          No results found.
        </Box>
      )}

      {filteredMembers.length > 0 && (
        <PaginationContainer>
          <Flex
            alignItems="center"
            style={{ gap: `${theme.space[3]}px`, position: 'relative' }}
            className="page-size-controls"
          >
            <Text
              as="span"
              fontSize={theme.fontSizes.pRegular14}
              color={theme.colors.gray600}
            >
              Show
            </Text>
            <Select
              OptionsHeaderRenderCmp={null}
              options={PAGE_SIZE_OPTIONS}
              value={PAGE_SIZE_OPTIONS.find(
                (option) => option.value === pageSize,
              )}
              changeHandler={handlePageSizeChange}
              buttonVariant="outline"
              minWidth={`${theme.space[9]}px`}
            />
            <Text
              as="span"
              fontSize={theme.fontSizes.pRegular14}
              color={theme.colors.gray600}
            >
              per page
            </Text>
          </Flex>

          <Pagination
            page={page}
            setPage={setPage}
            totalCount={filteredMembers.length}
            pageSize={pageSize}
          />
        </PaginationContainer>
      )}
    </Box>
  )
}

export default MembersList
